// src/pages/FinanceiroRepresentantes.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import {
  Alert,
  Button,
  Modal,
  Spinner,
  Table,
  TextInput,
} from "flowbite-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiChevronRight,
  HiChevronDown,
  HiOutlineRefresh,
  HiOutlineSearch,
} from "react-icons/hi";
import { MdOutlineAccountTree } from "react-icons/md";
import { FaRegMoneyBill1, FaUsers } from "react-icons/fa6";
import Tree from "react-d3-tree";
import { CiGlobe } from "react-icons/ci";

/* ========================= Helpers ========================= */
const toMoney = (v = 0) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const displayName = (r = {}) =>
  r?.name ||
  r?.socialReason ||
  r?.fantasyName ||
  `Representante #${r?.id ?? "?"}`;

/* ========================= Paletas por nível =========================
   - LEVEL_PALETTE: cores de fundo/hover/anéis
   - LEVEL_ACCENTS: cor de destaque (borda esquerda, pino do conector, badge)
*/
const LEVEL_PALETTE = [
  { bg: "bg-sky-50", hover: "hover:bg-sky-100", ring: "ring-sky-200" },
  {
    bg: "bg-emerald-50",
    hover: "hover:bg-emerald-100",
    ring: "ring-emerald-200",
  },
  { bg: "bg-amber-50", hover: "hover:bg-amber-100", ring: "ring-amber-200" },
  { bg: "bg-purple-50", hover: "hover:bg-purple-100", ring: "ring-purple-200" },
  { bg: "bg-rose-50", hover: "hover:bg-rose-100", ring: "ring-rose-200" },
  { bg: "bg-cyan-50", hover: "hover:bg-cyan-100", ring: "ring-cyan-200" },
];

const LEVEL_ACCENTS = [
  {
    border: "border-blue-300",
    dotHex: "#7dd3fc", // sky-300
    badgeBg: "bg-blue-100",
    badgeText: "text-blue-800",
  },
  {
    border: "border-cyan-300",
    dotHex: "#6ee7b7",
    badgeBg: "bg-cyan-100",
    badgeText: "text-cyan-800",
  },
  {
    border: "border-amber-300",
    dotHex: "#fcd34d",
    badgeBg: "bg-amber-100",
    badgeText: "text-amber-800",
  },
  {
    border: "border-purple-300",
    dotHex: "#c4b5fd",
    badgeBg: "bg-purple-100",
    badgeText: "text-purple-800",
  },
  {
    border: "border-rose-300",
    dotHex: "#fda4af",
    badgeBg: "bg-rose-100",
    badgeText: "text-rose-800",
  },
  {
    border: "border-l-cyan-300",
    dotHex: "#67e8f9",
    badgeBg: "bg-cyan-100",
    badgeText: "text-cyan-800",
  },
];

function levelRowClasses(level = 0) {
  if (level === 0)
    return { bg: "bg-white", hover: "hover:bg-gray-50", ring: "ring-gray-200" };
  const idx = (level - 1) % LEVEL_PALETTE.length;
  return LEVEL_PALETTE[idx];
}
function levelAccent(level = 0) {
  if (level === 0)
    return {
      border: "border-l-gray-300",
      dotHex: "#d1d5db",
      badgeBg: "bg-gray-100",
      badgeText: "text-gray-800",
    };
  const idx = (level - 1) % LEVEL_ACCENTS.length;
  return LEVEL_ACCENTS[idx];
}

/* ========================= Badges ========================= */
// Status: 1=Ativo, -1=Inadimplente, 0=Bloqueado
const tinyStatusBadge = (status) => {
  if (status === undefined || status === null) return null;
  const map = {
    1: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Ativo" },
    0: { bg: "bg-gray-200", text: "text-gray-700", label: "Bloqueado" },
    "-1": { bg: "bg-amber-100", text: "text-amber-700", label: "Inadimplente" },
  };
  const key = String(status);
  const conf = map[key] || {
    bg: "bg-sky-100",
    text: "text-sky-700",
    label: String(status),
  };
  return (
    <span
      className={`ml-1 inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-medium ${conf.bg} ${conf.text} border border-black/5`}
    >
      {conf.label}
    </span>
  );
};

const LevelBadge = ({ level = 0 }) => {
  const acc = levelAccent(level);
  return (
    <span
      className={`ml-2 inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-semibold ${acc.badgeBg} ${acc.badgeText} border border-black/5`}
    >
      Nível {level}
    </span>
  );
};

/* ========================= Build/Filter Tree ========================= */
function buildTreeFlexible(list = [], rootId, rootLabel = "Sua Empresa") {
  const byParent = new Map();
  list.forEach((n) => {
    const p = n.parentId ?? null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(n);
  });
  const attach = (node) => {
    const kids = byParent.get(node.id) || [];
    return { ...node, children: kids.map(attach) };
  };
  if (rootId != null) {
    const rootNode = list.find((n) => n.id === rootId);
    if (rootNode) return [attach(rootNode)];
    const directKids = (byParent.get(rootId) || []).map(attach);
    return [
      {
        id: rootId,
        name: rootLabel,
        status: 1,
        children: directKids,
        __virtualRoot: true,
      },
    ];
  }
  const roots = byParent.get(null) || byParent.get(undefined) || [];
  return roots.map(attach);
}

function filterTreeByText(nodes, text) {
  if (!text) return nodes;
  const t = text.toLowerCase();
  const matchNode = (n) =>
    displayName(n).toLowerCase().includes(t) ||
    String(n?.id ?? "")
      .toLowerCase()
      .includes(t);
  const recur = (ns) => {
    const out = [];
    for (const n of ns) {
      const kids = Array.isArray(n.children) ? recur(n.children) : [];
      if (matchNode(n) || kids.length) out.push({ ...n, children: kids });
    }
    return out;
  };
  return recur(nodes);
}

/* ========================= Modal: Clientes Diretos ========================= */
function DirectClientsModal({ open, onClose, rep }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open || !rep?.id) return;
    (async () => {
      try {
        setLoading(true);
        const url = `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1&companyTypeId=CLIENTE&representativeId=${rep.id}`;
        const { data } = await axios.get(url);
        const raw = data?.data?.data ?? data?.data ?? data;
        setRows(Array.isArray(raw) ? raw : raw ? [raw] : []);
      } catch (e) {
        console.error("DirectClientsModal:", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, rep?.id]);

  return (
    <Modal show={open} size="5xl" onClose={onClose} dismissible>
      <Modal.Header>
        Clientes diretos — {displayName(rep)} (ID {rep?.id})
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="py-10 flex justify-center">
            <Spinner size="xl" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-600">
            Nenhum cliente direto encontrado.
          </p>
        ) : (
          <div className="overflow-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Nome</Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {rows.map((c) => (
                  <Table.Row key={c.id} className="bg-white">
                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900 py-1">
                      <div>
                        {c.name ||
                          c.socialReason ||
                          c.fantasyName ||
                          `Cliente #${c.id}`}
                      </div>
                      <div className="text-xs text-gray-600">
                        {c.document || "—"}
                      </div>
                    </Table.Cell>
                    <Table.Cell>{tinyStatusBadge(c.status)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ========================= Métricas (caso precise) ========================= */
function aggregate(node) {
  let count = 1;
  if (node?.children?.length)
    node.children.forEach((c) => (count += aggregate(c).count));
  return { count };
}

/* ========================= VISUALIZAÇÃO VERTICAL COMPACTA ========================= */
// Aumentei a indentação para dar mais respiro entre níveis
const INDENT = 20;

/** Conectores visuais entre pai/filhos, agora com o pino colorido por nível */
function NodeConnectors({
  level = 0,
  lineageKeeps = [],
  isLast,
  pinColor = "#d1d5db",
}) {
  if (level === 0) return null;
  return (
    <div className="absolute inset-y-0 left-0 pointer-events-none">
      {/* Linhas de continuidade das colunas anteriores */}
      {lineageKeeps.slice(0, -1).map((keep, i) => (
        <div
          key={i}
          className="absolute top-0 h-full"
          style={{ left: 8 + i * INDENT, width: INDENT }}
        >
          {keep && <div className="mx-auto h-full w-px bg-white" />}
        </div>
      ))}
      {/* Joelho do nível atual */}
      <div
        className="absolute"
        style={{ left: 8 + (level - 1) * INDENT, top: 22 }}
      >
        {!isLast && (
          <div className="absolute left-1/2 -translate-x-1/2 -top-12 h-12 w-px bg-white" />
        )}
        <div
          className="h-px"
          style={{
            width: INDENT,
            background: "#e5e7eb",
            transform: `translateX(${INDENT / 2}px)`,
          }}
        />
        {/* Pino colorido por nível */}
        <div
          className="absolute -left-[4px] -top-1 h-3 w-3 rounded-full ring-2 ring-white"
          style={{
            background: pinColor,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.04)",
          }}
        />
      </div>
    </div>
  );
}

/** Item visual (cartão) com rail/borda colorida por nível e badge de nível */
function VisualNode({
  node,
  level = 0,
  lineageKeeps = [],
  isLast = false,
  forceOpenAll = false,
  searchText,
  parent,
  onOpenDirectClients,
  dense = true,
}) {
  const [open, setOpen] = useState(!!forceOpenAll);
  useEffect(() => setOpen(!!forceOpenAll), [forceOpenAll]);

  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const directClients =
    node?.directClientsCount ??
    node?.totalDirectClients ??
    node?.directCustomers ??
    0;
  const monthUsage =
    node?.monthUsage ?? node?.usageMonth ?? node?.usage?.monthTotal ?? 0;
  const childrenCount = node?.children?.length || 0;

  const palette = levelRowClasses(level);
  const accent = levelAccent(level);

  const highlight = (text, query) => {
    if (!query) return text;
    const i = text.toLowerCase().indexOf(query.toLowerCase());
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-yellow-200 rounded px-0.5">
          {text.slice(i, i + query.length)}
        </mark>
        {text.slice(i + query.length)}
      </>
    );
  };

  const childKeeps = [...lineageKeeps, !isLast];

  const padY = dense ? "py-2.5" : "py-3";
  const padX = dense ? "px-2.5" : "px-3.5";
  const textMain = dense ? "text-[13px]" : "text-[14px]";
  const textSub = dense ? "text-[11px]" : "text-[12px]";
  const chevronSize = dense ? "h-4 w-4" : "h-5 w-5";

  return (
    <div className="relative">
      <NodeConnectors
        level={level}
        lineageKeeps={lineageKeeps}
        isLast={isLast}
        pinColor={accent.dotHex}
      />

      <motion.div
        layout
        className={[
          "relative rounded-md border border-gray-200 transition",
          palette.hover,
          // rail/borda esquerda por nível
          accent.border,
          "shadow-sm",
        ].join(" ")}
        style={{
          marginLeft: 16 + level * INDENT,
          backgroundClip: "padding-box",
        }}
      >
        {/* faixa suave no topo para reforçar o nível */}
        {level > 0 && (
          <div
            className="absolute inset-x-0 top-0 h-1 rounded-t-md opacity-70"
            style={{ background: accent.dotHex }}
          />
        )}

        {/* Linha principal */}
        <div
          className={`flex items-center justify-between ${padX} ${padY} bg-white/95`}
        >
          <div className="min-w-0 flex items-center gap-2">
            {hasChildren && (
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`rounded-sm border border-gray-300 bg-gray-500 text-white flex items-center justify-center ${
                  dense ? "h-5 w-5" : "h-6 w-6"
                } hover:bg-gray-600`}
                title={open ? "Recolher" : "Expandir"}
              >
                {open ? (
                  <HiChevronDown className={chevronSize} />
                ) : (
                  <HiChevronRight className={chevronSize} />
                )}
              </button>
            )}

            <div className="min-w-0">
              <div
                className={`font-semibold text-gray-900 truncate ${textMain}`}
              >
                {highlight(displayName(node), searchText)}
                {tinyStatusBadge(node?.status)}
                <LevelBadge level={level} />
              </div>

              <div
                className={`text-gray-600 flex items-center gap-2 flex-wrap ${textSub} mt-2`}
              >
                <code className="rounded bg-gray-100 px-1.5 py-[1px] text-gray-700">
                  #{node.id}
                </code>

                {childrenCount > 0 && (
                  <span className="text-gray-600">
                    {childrenCount} filho{childrenCount > 1 ? "s" : ""}
                  </span>
                )}

                <button
                  type="button"
                  onClick={() => onOpenDirectClients?.(node)}
                  className="text-gray-700 hover:underline"
                  title="Ver clientes diretos"
                >
                  <span className="inline-flex items-center gap-1">
                    <FaUsers className="h-3.5 w-3.5" />
                    <b>{directClients}</b> clientes
                  </span>
                </button>

                {node?.representative?.url && (
                  <span className="inline-flex items-center gap-1">
                    <CiGlobe className="h-3.5 w-3.5" />
                    {node?.representative?.url}
                  </span>
                )}

                {node?.representative?.token_asaas && (
                  <span className="inline-flex items-center gap-1">
                    <FaRegMoneyBill1 className="h-3.5 w-3.5" /> Asaas
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="shrink-0 text-right">
            <div className={`${textSub} text-gray-500 leading-none`}>
              Uso no mês
            </div>
            <div
              className={`${
                dense ? "text-[13px]" : "text-sm"
              } font-semibold text-gray-900`}
            >
              {toMoney(monthUsage)}
            </div>
          </div>
        </div>
      </motion.div>

      {/* FILHOS */}
      <AnimatePresence initial={false}>
        {hasChildren && open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="space-y-1.5 mt-1"
          >
            {node.children.map((c, idx) => (
              <VisualNode
                key={c.id}
                node={c}
                level={level + 1}
                lineageKeeps={childKeeps}
                isLast={idx === node.children.length - 1}
                forceOpenAll={forceOpenAll}
                searchText={searchText}
                parent={node}
                onOpenDirectClients={onOpenDirectClients}
                dense={dense}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Raiz da visualização vertical */
function VerticalTreeVisual({
  trees,
  searchText,
  forceOpenAll,
  onOpenDirectClients,
}) {
  const roots = Array.isArray(trees) ? trees : [];
  const [dense, setDense] = useState(true);

  const meta = useMemo(() => {
    let nodes = 0;
    let usage = 0;
    const walk = (n) => {
      nodes += 1;
      usage += Number(
        n?.monthUsage ?? n?.usageMonth ?? n?.usage?.monthTotal ?? 0
      );
      n?.children?.forEach(walk);
    };
    roots.forEach(walk);
    return { nodes, usage };
  }, [roots]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <div className="text-[11px] text-gray-700 font-semibold">
          Hierarquia (vertical) — {meta.nodes} nós · {toMoney(meta.usage)}/mês
        </div>
      </div>

      {roots.length === 0 ? (
        <div className="p-6 text-sm text-center text-gray-500">
          Nenhum representante encontrado.
        </div>
      ) : (
        <div className={`p-2 ${dense ? "space-y-1.5" : "space-y-2.5"}`}>
          {roots.map((root, idx) => (
            <VisualNode
              key={root.id}
              node={root}
              level={0}
              lineageKeeps={[]}
              isLast={idx === roots.length - 1}
              forceOpenAll={forceOpenAll}
              searchText={searchText}
              parent={null}
              onOpenDirectClients={onOpenDirectClients}
              dense={dense}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ========================= ORGANOGRAMA (react-d3-tree) ========================= */
function toD3Node(n) {
  return {
    name: displayName(n),
    attributes: {
      ID: String(n.id),
      Status: String(n?.status ?? "—"),
      "Clientes diretos": String(
        n?.directClientsCount ??
          n?.totalDirectClients ??
          n?.directCustomers ??
          0
      ),
      "Uso no mês": toMoney(
        n?.monthUsage ?? n?.usageMonth ?? n?.usage?.monthTotal ?? 0
      ),
    },
    children: Array.isArray(n.children) ? n.children.map(toD3Node) : undefined,
    _raw: n,
  };
}
function treesToData(trees) {
  return (trees || []).map(toD3Node);
}

const foreignObjectProps = { width: 260, height: 130, x: -130, y: -65 };
function NodeCard({ nodeDatum, toggleNode }) {
  const raw = nodeDatum._raw || {};
  const directClients =
    raw?.directClientsCount ??
    raw?.totalDirectClients ??
    raw?.directCustomers ??
    0;
  const monthUsage =
    raw?.monthUsage ?? raw?.usageMonth ?? raw?.usage?.monthTotal ?? 0;
  return (
    <foreignObject {...foreignObjectProps}>
      <div
        onClick={toggleNode}
        className="rounded-xl border border-gray-200 bg-white shadow-sm px-3 py-2 w-[240px] cursor-pointer select-none"
      >
        <div className="text-[13px] font-semibold text-gray-800 truncate">
          {raw.name || nodeDatum.name}
        </div>
        <div className="text-[11px] text-gray-500">ID: {raw.id}</div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
          <div>
            <div className="text-gray-500">Clientes diretos</div>
            <div className="font-semibold">{directClients}</div>
          </div>
          <div>
            <div className="text-gray-500">Uso no mês</div>
            <div className="font-semibold">{toMoney(monthUsage)}</div>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-500">
          <span>Status:</span> {tinyStatusBadge(raw?.status)}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">
          Clique p/ expandir/colapsar
        </div>
      </div>
    </foreignObject>
  );
}

function OrgChartD3({ trees }) {
  const data = useMemo(() => treesToData(trees), [trees]);
  const containerRef = useRef(null);
  const [translate, setTranslate] = useState({ x: 400, y: 100 });
  const [zoom, setZoom] = useState(0.8);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const resize = () => {
      const w = el.clientWidth || 800;
      setTranslate({ x: w / 2, y: 100 });
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!trees?.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
        Nenhum representante encontrado.
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white overflow-hidden"
      style={{ height: "600px" }}
    >
      <div ref={containerRef} className="w-full" style={{ height: "600px" }}>
        <div className="flex justify-end p-2 gap-2 bg-cyan-50 border-b border-cyan-100">
          <Button
            size="xs"
            color="light"
            onClick={() => setZoom((z) => Math.min(1.8, z + 0.1))}
          >
            + Zoom
          </Button>
          <Button
            size="xs"
            color="light"
            onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))}
          >
            − Zoom
          </Button>
          <Button
            size="xs"
            color="light"
            onClick={() =>
              setTranslate({
                x: (containerRef.current?.clientWidth || 800) / 2,
                y: 100,
              })
            }
          >
            Centralizar
          </Button>
        </div>
        <Tree
          data={data}
          orientation="vertical"
          translate={translate}
          zoom={zoom}
          zoomable
          collapsible
          separation={{ siblings: 1.0, nonSiblings: 1.2 }}
          nodeSize={{ x: 280, y: 160 }}
          pathFunc="elbow"
          renderCustomNodeElement={(rd) => <NodeCard {...rd} />}
          enableLegacyTransitions={false}
        />
      </div>
    </div>
  );
}

/* ========================= Pie Chart (SVG) ========================= */
function PieChart({
  data = [],
  size = 240,
  strokeWidth = 24,
  unit = "clientes",
}) {
  const radius = size / 2;
  const innerRadius = radius - strokeWidth;
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;
  let acc = -Math.PI / 2;
  const segments = data.map((d) => {
    const val = Number(d.value) || 0;
    const angle = (val / total) * Math.PI * 2;
    const start = acc;
    const end = acc + angle;
    acc = end;
    return { ...d, start, end };
  });
  const polar = (cx, cy, r, a) => ({
    x: cx + r * Math.cos(a),
    y: cy + r * Math.sin(a),
  });
  const arc = (cx, cy, r, r2, a0, a1) => {
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p1 = polar(cx, cy, r, a0),
      p2 = polar(cx, cy, r, a1),
      p3 = polar(cx, cy, r2, a1),
      p4 = polar(cx, cy, r2, a0);
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 1 ${p2.x} ${p2.y} L ${p3.x} ${p3.y} A ${r2} ${r2} 0 ${large} 0 ${p4.x} ${p4.y} Z`;
  };
  const palette = (i) => {
    const hues = [210, 150, 265, 12, 190, 330, 95, 42];
    return `hsl(${hues[i % hues.length]} 70% 50%)`;
  };
  return (
    <div className="rounded-2xl p-4 shadow-md bg-white border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm text-gray-600">Representatividade</p>
          <h3 className="text-lg font-semibold text-gray-900">
            Clientes diretos por representante
          </h3>
          <p className="text-xs text-gray-500">
            Total: {total} {unit}
          </p>
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments.map((s, i) => (
            <path
              key={i}
              d={arc(radius, radius, radius - 4, innerRadius, s.start, s.end)}
              fill={palette(i)}
              opacity={0.95}
            />
          ))}
          <circle cx={radius} cy={radius} r={innerRadius - 0.5} fill="#fff" />
        </svg>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <span
                className="inline-block h-3 w-3 rounded"
                style={{ background: palette(i) }}
              />
              <div className="text-sm text-gray-800 truncate">
                <span className="font-medium">{d.label}</span>
                <span className="text-gray-500">
                  {" "}
                  — {d.value} {unit}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ========================= Tabs (Segmented) ========================= */
function SegmentedTabs({ active, onChange }) {
  const tabs = [
    { id: 0, label: "Lista vertical (compacta)" },
    { id: 1, label: "Organograma" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Visualização de representantes"
      className="relative inline-flex rounded-xl border border-gray-300 bg-white p-1 shadow-sm"
    >
      <div className="absolute inset-y-1 left-1 w-[calc(50%-0.5rem)]">
        <motion.div
          layout
          className="h-full rounded-lg bg-gray-900/90"
          style={{
            width: "calc(50% - 0rem)",
            translateX: active === 0 ? "0%" : "100%",
          }}
          transition={{ type: "spring", stiffness: 350, damping: 30 }}
        />
      </div>
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={active === t.id}
          aria-controls={`tab-panel-${t.id}`}
          id={`tab-${t.id}`}
          onClick={() => onChange(t.id)}
          className={[
            "relative z-10 px-4 py-2 text-sm font-semibold rounded-lg transition-colors",
            active === t.id
              ? "text-white bg-blue-800"
              : "text-gray-700 hover:text-gray-900",
          ].join(" ")}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ========================= Página ========================= */
export default function FinancialRepresentative() {
  const { user } = useApplication();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [forceOpenAll, setForceOpenAll] = useState(false);
  const [modalRep, setModalRep] = useState(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["representativesAll", user?.companyId],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES_METRICS}?itemsPerPage=-1&_=${Date.now()}`
      );
      const raw = data?.data?.data ?? data?.data ?? data;
      const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const normalized = arr.map((n) => ({
        ...n,
        parentId:
          n.parentId ?? n?.ParentCompany?.id ?? n?.representativeId ?? null,
        name:
          n?.name ?? n?.socialReason ?? n?.fantasyName ?? `ID ${n?.id ?? "?"}`,
        status: n?.status ?? n?.situation ?? n?.state ?? 1,
        monthUsage: n?.monthUsage ?? n?.usageMonth ?? n?.usage?.monthTotal ?? 0,
        directClientsCount:
          n?.directClientsCount ??
          n?.totalDirectClients ??
          n?.directCustomers ??
          0,
        representative: n?.representative ?? n?.Representative ?? null,
      }));
      return normalized;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    }
  );

  const trees = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    if (!user?.companyId) return [];
    return buildTreeFlexible(
      list,
      user.companyId,
      user?.Company?.name || user?.Company?.socialReason || "Sua Empresa"
    );
  }, [data, user?.companyId, user?.Company]);

  const filteredTrees = useMemo(
    () => filterTreeByText(trees, search),
    [trees, search]
  );

  const pieData = useMemo(() => {
    const root = filteredTrees?.[0];
    if (!root || !Array.isArray(root.children)) return [];
    const rows = root.children.map((c) => ({
      label: displayName(c),
      value:
        Number(
          c?.directClientsCount ??
            c?.totalDirectClients ??
            c?.directCustomers ??
            0
        ) || 0,
    }));
    return rows.filter((r) => r.value > 0).sort((a, b) => b.value - a.value);
  }, [filteredTrees]);

  const kpis = useMemo(() => {
    let nodes = 0,
      sumUsage = 0;
    const walk = (n) => {
      nodes += 1;
      sumUsage += Number(
        n?.monthUsage ?? n?.usageMonth ?? n?.usage?.monthTotal ?? 0
      );
      n?.children?.forEach(walk);
    };
    filteredTrees.forEach((root) => walk(root));
    return { nodes, sumUsage };
  }, [filteredTrees]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Financeiro — Representantes
          </h1>
          <p className="text-sm text-gray-500">
            Visualize os representantes abaixo da sua empresa, em lista vertical
            compacta e como organograma.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={HiOutlineSearch}
            placeholder="Buscar por nome ou ID…"
          />
          <Button color="gray" onClick={() => refetch()} title="Atualizar">
            <HiOutlineRefresh className={isFetching ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <PieChart data={pieData} size={260} strokeWidth={22} unit="clientes" />

      {/* Tabs + Ações */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <SegmentedTabs active={activeTab} onChange={setActiveTab} />
        {activeTab === 0 && (
          <div className="inline-flex rounded-md overflow-hidden border border-gray-300">
            <Button
              color={forceOpenAll ? "gray" : "light"}
              onClick={() => setForceOpenAll(true)}
              className="rounded-none"
            >
              Expandir tudo
            </Button>
            <Button
              color={!forceOpenAll ? "gray" : "light"}
              onClick={() => setForceOpenAll(false)}
              className="rounded-none"
            >
              Recolher tudo
            </Button>
          </div>
        )}
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 flex items-center justify-center">
          <Spinner size="xl" />
        </div>
      ) : error ? (
        <Alert color="failure">
          Ocorreu um erro ao carregar os representantes.
        </Alert>
      ) : activeTab === 0 ? (
        <VerticalTreeVisual
          trees={filteredTrees}
          searchText={search}
          forceOpenAll={forceOpenAll}
          onOpenDirectClients={setModalRep}
        />
      ) : (
        <OrgChartD3 trees={filteredTrees} />
      )}

      {/* Modal de clientes diretos */}
      <DirectClientsModal
        open={!!modalRep}
        onClose={() => setModalRep(null)}
        rep={modalRep}
      />

      <div
        id="tab-panel-0"
        role="tabpanel"
        aria-labelledby="tab-0"
        hidden={activeTab !== 0}
      />
      <div
        id="tab-panel-1"
        role="tabpanel"
        aria-labelledby="tab-1"
        hidden={activeTab !== 1}
      />
    </div>
  );
}
